import { Trans, useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';

import { useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { useResaleAgreement } from '../../../../hooks/useResaleAgreement';
import { useAuth } from '../../../../providers';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { usePurchase } from '../../../../providers/PurchaseProvider';
import { getFirstAndLastWords } from '../../../../utils/strings';
import { PaymentType } from '../../types';

interface Props {
  payWithCrypto: boolean;
  productTags?: string[];
  productTitle: string;
  isPreOrderOptIn: boolean;
}

const copies = {
  preOrder: {
    legalDisclaimerOnEnter: 'drawers.review.draw.legalDisclaimerOnPreOrder',
    legalDisclaimerOnEnterResaleNonCard:
      'drawers.review.draw.legalDisclaimerOnPreOrderResaleNonCard',
  },
  auction: {
    legalDisclaimerOnEnter: 'drawers.review.draw.legalDisclaimerOnAuction',
    legalDisclaimerOnEnterResaleNonCard:
      'drawers.review.draw.legalDisclaimerOnAuctionResaleNonCard',
  },
  draw: {
    legalDisclaimerOnEnter: 'drawers.review.draw.legalDisclaimerOnEnter',
    legalDisclaimerOnEnterResaleNonCard:
      'drawers.review.draw.legalDisclaimerOnEnterResaleNonCard',
  },
  drawPreOrderOptIn: {
    legalDisclaimerOnEnter:
      'drawers.review.draw.legalDisclaimerOnEnterPreOrderOptIn',
    legalDisclaimerOnEnterResaleNonCard:
      'drawers.review.draw.legalDisclaimerOnEnterResaleNonCard',
  },
};

const URL_TERMS_AND_CONDITIONS =
  'https://avantarte.com/support/terms-and-conditions#draws';

export const ReviewDrawFooter: FC<Props> = ({
  isPreOrderOptIn,
  payWithCrypto,
  productTags = [],
  productTitle,
}) => {
  const { t } = useTranslation('product');
  const purchasingController = usePurchase();
  const { draw, selectedPaymentType } = usePreAuthDrawCtx();
  const { user } = useAuth();
  const isPreOrder = useIsPreOrder(draw);

  const isCardPayment = selectedPaymentType === PaymentType.CARD;

  const [firstName, lastName] = getFirstAndLastWords(
    user?.fullName || undefined,
  );

  const resaleAgreementLink = useResaleAgreement({
    firstName,
    lastName,
    productTags,
    productTitle,
  });

  const selectedCopies = useMemo(() => {
    if (isPreOrder) {
      return copies.preOrder;
    }

    if (purchasingController.releaseType === 'auction') {
      return copies.auction;
    }

    if (isPreOrderOptIn) {
      return copies.drawPreOrderOptIn;
    }

    return copies.draw;
  }, [isPreOrder, isPreOrderOptIn, purchasingController.releaseType]);

  if (purchasingController.releaseType === 'auction') {
    return (
      <div className="px-6 text-center text-xs text-neutral-4">
        <Trans i18nKey="auction.auctionResaleFooter" t={t}>
          By entering the auction you also agree that you have read and accept
          our
          <a
            className="hyperlink"
            href={resaleAgreementLink}
            rel="noreferrer"
            target="_blank"
          >
            Resale agreement
          </a>
          .
        </Trans>
      </div>
    );
  }

  return (
    <div className="px-6 text-center text-xs text-neutral-4">
      {isCardPayment ? (
        <Trans i18nKey={selectedCopies.legalDisclaimerOnEnter} t={t}>
          By pre-ordering, you’re authorising Avant Arte to charge you the
          confirmed amount if your application is successful. A processing fee
          of up to 10% may be applied at our discretion, only if you cancel
          after a winning draw entry. And you agree that you have read and
          accept our
          <a
            className="hyperlink"
            href={resaleAgreementLink}
            rel="noreferrer"
            target="_blank"
          >
            Resale Agreement
          </a>
        </Trans>
      ) : null}
      {/* Exclusive check for false to make sure no product doesn't mean no RA */}
      {/* eslint-disable-next-line no-nested-ternary */}
      {!isPreOrder ? (
        purchasingController.product?.noResaleAgreement === false ? (
          <>
            {isCardPayment || payWithCrypto ? (
              <Trans
                i18nKey="drawers.review.draw.legalDisclaimerOnEnterResale"
                t={t}
              >
                And you agree that you have read and accept our
                <a
                  className="hyperlink"
                  href={resaleAgreementLink}
                  rel="noreferrer"
                  target="_blank"
                >
                  Resale Agreement
                </a>{' '}
                and
                <a
                  className="hyperlink"
                  href={URL_TERMS_AND_CONDITIONS}
                  rel="noreferrer"
                  target="_blank"
                >
                  Draw terms and conditions
                </a>
                .
              </Trans>
            ) : (
              <Trans
                i18nKey={selectedCopies.legalDisclaimerOnEnterResaleNonCard}
                t={t}
              >
                By entering the draw you agree that you have read and accept
                our&nbsp;
                <a
                  className="hyperlink"
                  href={resaleAgreementLink}
                  rel="noreferrer"
                  target="_blank"
                >
                  Resale Agreement
                </a>
                &nbsp; if you win.
              </Trans>
            )}
          </>
        ) : (
          <>
            {isCardPayment || payWithCrypto ? (
              <Trans
                i18nKey="drawers.review.draw.legalDisclaimerOnEnterNoResale"
                t={t}
              >
                And you agree that you have read and accept our
                <a
                  className="hyperlink"
                  href={URL_TERMS_AND_CONDITIONS}
                  rel="noreferrer"
                  target="_blank"
                >
                  Draw terms and conditions
                </a>
                .
              </Trans>
            ) : (
              <Trans
                i18nKey={selectedCopies.legalDisclaimerOnEnterResaleNonCard}
                t={t}
              >
                By entering the draw you agree that you have read and accept
                our&nbsp;
                <a
                  className="hyperlink"
                  href={resaleAgreementLink}
                  rel="noreferrer"
                  target="_blank"
                >
                  Resale Agreement
                </a>
                &nbsp; if you win.
              </Trans>
            )}
          </>
        )
      ) : null}
      {purchasingController.releaseType === 'draw-release' && !isPreOrder && (
        <Trans i18nKey="drawers.review.draw.moreInfo" t={t}>
          For more information on how draws work, see our&nbsp;
          <a
            className="hyperlink"
            href="/support/releases#what-is-a-draw-release"
            rel="noreferrer"
            target="_blank"
          >
            FAQs
          </a>
        </Trans>
      )}
    </div>
  );
};
